import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Radio, CircleDot, WifiOff, Loader2, Ban, Eye, AlertTriangle, CheckCircle2,
  ArrowUpRight, Filter, X, Search, Volume2, VolumeX,
} from "lucide-react";
import useDenyChime from "../lib/useDenyChime";

const actionMeta = {
  allow:  { color: "#22c55e", icon: CheckCircle2,   label: "ALLOW"  },
  deny:   { color: "#ef4444", icon: Ban,            label: "DENY"   },
  redact: { color: "#f59e0b", icon: Eye,            label: "REDACT" },
  flag:   { color: "#FF6B35", icon: AlertTriangle,  label: "FLAG"   },
};

const statusMeta = {
  live:         { color: "#22c55e", label: "LIVE",         Icon: Radio   },
  connecting:   { color: "#FFD98A", label: "CONNECTING",   Icon: Loader2 },
  reconnecting: { color: "#FFD98A", label: "RECONNECTING", Icon: Loader2 },
  offline:      { color: "#94a3b8", label: "OFFLINE",      Icon: WifiOff },
};

const ACTIONS = ["allow", "deny", "redact", "flag"];
const FILTER_STORAGE_KEY = "sentry_live_feed_filters_v1";

function loadStoredFilters() {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      actions: Array.isArray(parsed.actions) ? parsed.actions.filter((a) => ACTIONS.includes(a)) : [],
      model: typeof parsed.model === "string" ? parsed.model : "",
      query: typeof parsed.query === "string" ? parsed.query : "",
    };
  } catch { return null; }
}

export default function LiveFeed({ status, decisions, lastAt, subscribers }) {
  const s = statusMeta[status] || statusMeta.offline;
  const StatusIcon = s.Icon;

  const [filters, setFilters] = useState(() => loadStoredFilters() || { actions: [], model: "", query: "" });
  const chime = useDenyChime(decisions);

  useEffect(() => {
    try { localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters)); } catch { /* noop */ }
  }, [filters]);

  // Model list derived from what we've actually seen in this session
  const models = useMemo(() => {
    const set = new Set();
    for (const d of decisions) if (d?.model) set.add(d.model);
    return Array.from(set).sort();
  }, [decisions]);

  const filtered = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    return decisions.filter((d) => {
      if (filters.actions.length && !filters.actions.includes(d.action)) return false;
      if (filters.model && d.model !== filters.model) return false;
      if (q) {
        const hay = `${d.prompt || ""} ${d.response || ""} ${(d.reasons || []).map((r) => r.policy_name).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [decisions, filters]);

  const toggleAction = (a) => {
    setFilters((f) => ({
      ...f,
      actions: f.actions.includes(a) ? f.actions.filter((x) => x !== a) : [...f.actions, a],
    }));
  };

  const clearFilters = () => setFilters({ actions: [], model: "", query: "" });
  const anyFilter = filters.actions.length > 0 || filters.model || filters.query;

  return (
    <div data-testid="live-feed" className="rounded-xl bg-white/5 border border-white/10 p-5 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <StatusIcon
              size={14}
              className={status === "connecting" || status === "reconnecting" ? "animate-spin" : ""}
              style={{ color: s.color }}
            />
            <div className="eyebrow" style={{ fontSize: 10, color: s.color, letterSpacing: 1.5 }}>{s.label}</div>
            {status === "live" && <span className="pulse-dot" />}
          </div>
          <div className="font-display text-lg mt-1">Live decision stream</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="live-deny-chime-toggle"
            type="button"
            onClick={chime.toggle}
            title={chime.enabled ? "Deny chime enabled — click to mute" : "Enable subtle chime on DENY events"}
            className={`relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-mono font-bold border transition ${
              chime.enabled
                ? "border-red-500/40 bg-red-500/10 text-red-300"
                : "border-white/10 text-cream/60 hover:text-cream hover:border-white/20"
            }`}
            aria-pressed={chime.enabled}
          >
            {chime.enabled ? <Volume2 size={11} /> : <VolumeX size={11} />}
            <span>{chime.enabled ? "CHIME ON" : "CHIME"}</span>
            {chime.enabled && <span className="pulse-dot-orange !w-1.5 !h-1.5" style={{ background: "#ef4444" }} />}
          </button>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-cream/50">Watching</div>
            <div className="text-sm text-cream/85 font-mono" data-testid="live-subscribers">{subscribers || 1}</div>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="rounded-lg bg-white/[0.02] border border-white/10 p-3 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-[11px] text-cream/60 mr-1">
            <Filter size={12} /> Actions
          </div>
          {ACTIONS.map((a) => {
            const m = actionMeta[a];
            const Icon = m.icon;
            const active = filters.actions.includes(a);
            return (
              <button
                key={a}
                data-testid={`live-filter-action-${a}`}
                type="button"
                onClick={() => toggleAction(a)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-mono font-bold border transition ${
                  active
                    ? "border-transparent text-[#0A0E1A]"
                    : "border-white/10 text-cream/60 hover:text-cream hover:border-white/20"
                }`}
                style={active ? { background: m.color } : { background: "transparent" }}
              >
                <Icon size={10} /> {m.label}
              </button>
            );
          })}

          <span className="hidden sm:block w-px h-4 bg-white/10 mx-1" />

          {/* Model select */}
          <div className="relative">
            <select
              data-testid="live-filter-model"
              value={filters.model}
              onChange={(e) => setFilters((f) => ({ ...f, model: e.target.value }))}
              className="pl-2 pr-6 py-1 rounded-full text-[10px] font-mono bg-transparent border border-white/10 text-cream/80 hover:border-white/20 outline-none appearance-none cursor-pointer"
              disabled={models.length === 0}
            >
              <option value="" className="bg-[#0A0E1A]">
                {models.length === 0 ? "no models yet" : "All models"}
              </option>
              {models.map((m) => (
                <option key={m} value={m} className="bg-[#0A0E1A]">{m}</option>
              ))}
            </select>
          </div>

          {/* Query */}
          <div className="relative flex-1 min-w-[140px]">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-cream/40" />
            <input
              data-testid="live-filter-query"
              value={filters.query}
              onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
              placeholder="search prompt / policy…"
              className="w-full pl-6 pr-2 py-1 rounded-full text-[10px] font-mono bg-transparent border border-white/10 text-cream/80 placeholder:text-cream/30 focus:border-orange focus:outline-none"
            />
          </div>

          {anyFilter && (
            <button
              data-testid="live-filter-clear"
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-[10px] text-cream/50 hover:text-cream px-2"
              title="Clear filters"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>

        {/* Match count */}
        <div className="mt-2 flex items-center justify-between text-[10px] text-cream/50">
          <span data-testid="live-match-count">
            {anyFilter
              ? <>Showing <b className="text-cream/80">{filtered.length}</b> of {decisions.length} decisions</>
              : <>{decisions.length} decisions in this session</>}
          </span>
          {anyFilter && filters.actions.length === 1 && filters.actions[0] === "deny" && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-400">
              <Ban size={10} /> DENY WATCH
            </span>
          )}
        </div>
      </div>

      {/* Rows */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {decisions.length === 0 ? (
          <EmptyState status={status} />
        ) : filtered.length === 0 ? (
          <NoMatches onClear={clearFilters} />
        ) : (
          <ul className="space-y-1.5">
            {filtered.slice(0, 8).map((d) => (
              <FeedRow key={d.id} d={d} />
            ))}
          </ul>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-[11px] text-cream/50">
        <span data-testid="live-last-at">
          {lastAt ? `Last event · ${new Date(lastAt).toLocaleTimeString()}` : "Awaiting first event…"}
        </span>
        <Link to="/app/decisions" className="inline-flex items-center gap-1 text-cream/70 hover:text-cream" data-testid="live-view-all">
          View all <ArrowUpRight size={11} />
        </Link>
      </div>
    </div>
  );
}

function FeedRow({ d }) {
  const meta = actionMeta[d.action] || actionMeta.allow;
  const Icon = meta.icon;
  const time = d.timestamp ? new Date(d.timestamp).toLocaleTimeString(undefined, { hour12: false }) : "--:--:--";
  const reason = d.reasons?.[0]?.policy_name || (d.action === "allow" ? "no policy hit" : "policy match");
  return (
    <li
      data-testid={`live-row-${d.id}`}
      className="live-row flex items-center gap-3 text-[12px] px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5"
    >
      <span className="text-cream/40 font-mono shrink-0">{time}</span>
      <span
        className="shrink-0 inline-flex items-center gap-1 font-bold font-mono"
        style={{ color: meta.color, width: 68 }}
      >
        <Icon size={11} /> {meta.label}
      </span>
      <span className="text-cream/85 truncate flex-1">{truncate(d.prompt, 68)}</span>
      <span className="chip chip-navy text-[10px] font-mono hidden md:inline-flex">{reason}</span>
      <span className="text-cream/50 font-mono text-[11px] shrink-0 w-14 text-right">
        {typeof d.latency_ms === "number" ? `${d.latency_ms.toFixed(2)}ms` : "—"}
      </span>
    </li>
  );
}

function EmptyState({ status }) {
  return (
    <div data-testid="live-feed-empty" className="h-full flex flex-col items-center justify-center text-center py-10">
      <CircleDot size={28} className="text-cream/25 mb-3" />
      <div className="text-sm text-cream/70">
        {status === "live" ? "Waiting for the next decision…" : "Connecting to Sentry gateway…"}
      </div>
      <div className="text-[11px] text-cream/40 mt-1">
        <Link className="text-orange" to="/app/playground">Fire one from Playground</Link> to see it appear instantly.
      </div>
    </div>
  );
}

function NoMatches({ onClear }) {
  return (
    <div data-testid="live-feed-no-matches" className="h-full flex flex-col items-center justify-center text-center py-10">
      <Filter size={26} className="text-cream/25 mb-3" />
      <div className="text-sm text-cream/70">No live decisions match your filters yet.</div>
      <button
        type="button"
        onClick={onClear}
        className="mt-2 text-[11px] text-orange hover:text-[#D14F1F] inline-flex items-center gap-1"
        data-testid="live-feed-no-matches-clear"
      >
        <X size={11} /> Clear filters
      </button>
    </div>
  );
}

function truncate(s = "", n = 60) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
